
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../services/api';
import { format, isValid, parseISO, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CalendarioAgendamentos from '../components/CalendarioAgendamentos';
import ModalNovoAgendamento from '../components/ModalNovoAgendamento';
import Header from '../components/Header';

const MeusAgendamentos = () => {
  const [agendamentos, setAgendamentos] = useState([]);
  const [mensagem, setMensagem] = useState({ text: '', type: '' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('proximos');
  const [statusFilter, setStatusFilter] = useState('todos');

  const hoje = useMemo(() => new Date(), []);

  const isToday = useCallback(
    (date) => format(date, 'yyyy-MM-dd') === format(hoje, 'yyyy-MM-dd'),
    [hoje]
  );

  const carregarAgendamentos = useCallback(async () => {
    try {
      const response = await api.get('agendamentos/meus_agendamentos/');
      setAgendamentos(response.data || []);
      setMensagem({ text: '', type: '' });
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      setMensagem({
        text: 'Erro ao carregar agendamentos. Tente novamente mais tarde.',
        type: 'danger',
      });
    }
  }, []);

  useEffect(() => {
    carregarAgendamentos();
  }, [carregarAgendamentos]);

  const handleDateChange = (newDate) => {
    if (isValid(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const formatarDataExibicao = (data, hora) => {
    const dateObj = parseISO(`${data}T${hora}`);
    return isValid(dateObj)
      ? format(dateObj, "PPPP 'às' HH:mm", { locale: ptBR })
      : 'Data/hora inválida';
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'confirmado':
        return 'success';
      case 'pendente':
        return 'warning';
      case 'cancelado':
        return 'danger';
      case 'realizado':
        return 'primary';
      case 'nao_compareceu':
        return 'dark';
      default:
        return 'secondary';
    }
  };

  const formatarStatus = (status) => {
    return status.toLowerCase() === 'nao_compareceu'
      ? 'Não compareceu'
      : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const eventosFiltrados = useMemo(() => {
    let filtered = agendamentos;

    if (selectedDate) {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter((a) => a.data === selectedDateStr);
    }

    if (activeTab === 'proximos') {
      filtered = filtered.filter((a) => {
        const dateObj = parseISO(`${a.data}T${a.hora_inicio}`);
        return (
          (isAfter(dateObj, hoje) || isToday(dateObj)) &&
          a.status.toLowerCase() !== 'cancelado'
        );
      });
    } else {
      filtered = filtered.filter((a) => {
        const dateObj = parseISO(`${a.data}T${a.hora_inicio}`);
        return (
          isBefore(dateObj, hoje) ||
          (!isAfter(dateObj, hoje) && !isToday(dateObj)) ||
          a.status.toLowerCase() === 'cancelado'
        );
      });
    }

    if (statusFilter !== 'todos') {
      filtered = filtered.filter(
        (a) => a.status.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    return filtered;
  }, [agendamentos, selectedDate, activeTab, statusFilter, hoje, isToday]);

  const diasComAgendamento = useMemo(() => {
    return [...new Set(agendamentos.map((a) => a.data).filter(Boolean))];
  }, [agendamentos]);

  const realizarCheckin = async (id) => {
    try {
      await api.post(`agendamentos/${id}/checkin/`);
      setMensagem({ text: 'Confirmação realizado com sucesso.', type: 'success' });
      carregarAgendamentos();
    } catch (error) {
      console.error('Erro ao realizar confirmação:', error);
      setMensagem({ text: 'Erro ao realizar confirmação.', type: 'danger' });
    }
  };

  const cancelarAgendamento = async (id) => {
    try {
      await api.post(`agendamentos/${id}/cancelar/`);
      setMensagem({ text: 'Agendamento cancelado com sucesso.', type: 'success' });
      carregarAgendamentos();
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      setMensagem({ text: 'Erro ao cancelar agendamento.', type: 'danger' });
    }
  };

  return (
    <>
      <Header />
      <div className="container-fluid py-4" style={{ backgroundColor: '#F2F2F2', minHeight: '100vh' }}>
        <div className="mx-auto" style={{ maxWidth: window.innerWidth < 440 ? '100%' : '60%' }}>
          <h2 className="text-center fw-bold mb-4" style={{ color: '#4B32AD' }}>
            Meus Agendamentos
          </h2>

          {mensagem.text && (
            <div className={`alert alert-${mensagem.type} text-center`} role="alert">
              {mensagem.text}
            </div>
          )}

          <div className="d-flex justify-content-end mb-4">
            <button
              className="btn rounded-pill px-4"
              style={{ backgroundColor: '#2F80ED', color: '#fff' }}
              onClick={() => setShowModal(true)}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Novo Agendamento
            </button>
          </div>

          <section className="card p-4 mb-4 border-0 shadow">
            <h4 className="mb-3 fw-semibold" style={{ color: '#2F80ED' }}>
              <i className="bi bi-calendar3 me-2"></i> Calendário
            </h4>
            <div className="d-flex justify-content-center">
              <CalendarioAgendamentos
                data={selectedDate}
                onChange={handleDateChange}
                diasComAgendamento={diasComAgendamento}
              />
            </div>
          </section>

          <section className="mb-4">
            <div className="mb-3">
              <h4 className="fw-semibold mb-3" style={{ color: '#4B32AD' }}>
                <i className="bi bi-calendar-check me-2"></i>
                {selectedDate
                  ? `Agendamentos em ${format(selectedDate, 'PPPP', { locale: ptBR })}`
                  : activeTab === 'proximos'
                  ? 'Próximos agendamentos'
                  : 'Agendamentos passados'}
              </h4>

              <div className="d-flex flex-wrap gap-2 align-items-center">
                <div className="nav nav-tabs">
                  <button
                    className={`nav-link ${activeTab === 'proximos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('proximos')}
                  >
                    Próximos
                  </button>
                  <button
                    className={`nav-link ${activeTab === 'passados' ? 'active' : ''}`}
                    onClick={() => setActiveTab('passados')}
                  >
                    Passados
                  </button>
                </div>

                <select
                  className="form-select w-100 w-md-auto"
                  style={{ minWidth: '180px', maxWidth: '250px' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="todos">Todos status</option>
                  <option value="agendado">Agendado</option>
                  <option value="confirmado">Confirmado</option>
                  <option value="realizado">Realizado</option>
                  <option value="nao_compareceu">Não compareceu</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>

            {eventosFiltrados.length > 0 ? (
              <div className="row g-3">
                {eventosFiltrados.map((agendamento) => (
                  <div key={agendamento.id} className="col-12 col-md-6">
                    <div className="card h-100 shadow-sm" style={{ borderLeft: '3px solid #4B32AD' }}>
                      <div className="card-body">
                        <h5 className="card-title mb-2" style={{ color: '#2F80ED' }}>
                          <i className="bi bi-hospital me-2"></i>
                          {agendamento.especialidade_nome || 'Especialidade não especificada'}
                        </h5>
                        <p className="mb-1 text-muted">
                          <i className="bi bi-calendar-event me-2"></i>
                          {formatarDataExibicao(agendamento.data, agendamento.hora_inicio)}
                        </p>
                        <p className="mb-1 text-muted">
                          <i className="bi bi-geo-alt me-2"></i>
                          {agendamento.campus_endereco || 'Endereço não disponível'}
                        </p>

                        <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                          <span className={`badge bg-${getStatusColor(agendamento.status)} rounded-pill px-3 py-2`}>
                            {formatarStatus(agendamento.status)}
                          </span>

                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => cancelarAgendamento(agendamento.id)}
                          >
                            <i className="bi bi-x-circle me-1"></i>
                            Cancelar
                          </button>

                          {(() => {
                            const agora = new Date();
                            const inicio = parseISO(`${agendamento.data}T${agendamento.hora_inicio}`);
                            const fim = parseISO(`${agendamento.data}T${agendamento.hora_fim}`);
                            // O check-in ficará disponível exatamente 24 horas antes do início do agendamento
                            const inicioCheckin = new Date(inicio.getTime() - 24 * 60 * 60 * 1000);

                            if (
                              agendamento.status === 'agendado' &&
                              agora >= inicioCheckin &&
                              agora <= fim
                            ) {
                              return (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => realizarCheckin(agendamento.id)}
                                >
                                  <i className="bi bi-person-check me-1"></i>
                                  Confirmar consulta
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-5">
                <i className="bi bi-calendar-x fs-1"></i>
                <p className="mt-3 fs-5">Nenhum agendamento encontrado</p>
              </div>
            )}
          </section>

          <ModalNovoAgendamento
            show={showModal}
            onHide={() => setShowModal(false)}
            onAgendamentoCriado={carregarAgendamentos}
          />
        </div>
      </div>
    </>
  );
};

export default MeusAgendamentos;
